/**
 * One doctor's configuration page (docs/ux/registry.md): the readiness
 * panel first, then the panels, one editing at a time; every save is one
 * patch with the version read; the Active switch and the More menu carry
 * the two confirmed actions (deactivate, and stopping an offered type). The
 * phone reads, and edits the two workspace fields in a sheet.
 */

import { statusLabel, type ReportTypeKind, type Status } from '@beam/core/status';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { say } from '@/app/announcer';
import { ApiError } from '@/app/api';
import { useOnline } from '@/app/online';
import type { DoctorPatch, DoctorView, ReadinessCheck } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { PageHeader } from '@/components/shell/PageHeader';
import { useShellWidth } from '@/components/shell/context';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { usePipelineRows } from '@/features/pipeline/usePipelineData';
import { pluralise } from '@/lib/format';
import type { PanelSaveState } from './ConfigPanel';
import { ReadinessCard, ReadinessPanel } from './ReadinessPanel';
import { PANEL_FOR, isType, longType, metaLine, shortSentence, shortType } from './describe';
import { ChangeHistory, DoctorPanel, OnTheReportPanel, ProfilePanel, TemplatesPanel, WorkspaceFields, WorkspacePanel, type PanelId } from './panels';
import { refusalOf, useClinics, useDoctor, useSettleDoctor, useUpdateDoctor } from './useRegistry';
import { useTemplatesIndex } from './templates/useTemplates';

const PANEL_NAME: Record<PanelId, string> = { doctor: 'Doctor', report: 'On the report', templates: 'Report types and overlays', profile: 'Profile', workspace: 'Workspace' };
const IDLE: PanelSaveState = { saving: false, error: null, conflict: false, savedAt: null };
const FIELD_FOR: Record<ReadinessCheck['fix'], string> = { status: 'active', details: 'specialty', display: 'credentialLine', signature: 'signature', templates: 'offered-ime', reportTypes: 'offered-ime' };

export function DoctorPage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const online = useOnline();
  const offline = !online;
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const type: ReportTypeKind = isType(params.get('type')) ? (params.get('type') as ReportTypeKind) : 'ime';
  const setType = (t: ReportTypeKind) => setParams((p) => { p.set('type', t); return p; }, { replace: true });

  const query = useDoctor(slug);
  const clinics = useClinics();
  const view = query.data;
  const d = view?.doctor;
  const { settle, reload } = useSettleDoctor(slug);
  const update = useUpdateDoctor(slug);
  const templatesIndex = useTemplatesIndex();
  const hasOverlay = (t: ReportTypeKind) => (templatesIndex.data?.overlays ?? []).some((o) => o.doctorSlug === slug && o.reportType === t);

  const [open, setOpen] = useState<PanelId | null>(null);
  const [dirtyOpen, setDirtyOpen] = useState(false);
  const [focusField, setFocusField] = useState<string | null>(null);
  const [state, setState] = useState<PanelSaveState>(IDLE);
  const [discard, setDiscard] = useState<null | (() => void)>(null);
  const [deactivate, setDeactivate] = useState(false);
  const [dropType, setDropType] = useState<{ types: ReportTypeKind[]; patch: DoctorPatch } | null>(null);
  const [switching, setSwitching] = useState(false);
  const [sheet, setSheet] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = open !== null && dirtyOpen;

  // Leaving the route with a dirty panel asks first.
  const blocker = useBlocker(() => dirtyRef.current);
  useEffect(() => { if (blocker.state === 'blocked') setDiscard(() => () => blocker.proceed()); }, [blocker]);

  const matters = usePipelineRows();
  const dependants = useMemo(() => (matters.data ?? []).filter((m) => m.doctor.slug === slug && m.status >= 4 && m.status <= 6), [matters.data, slug]);

  function openPanel(id: PanelId, field: string | null = null) {
    if (open && open !== id) return;
    setState(IDLE); setFocusField(field); setOpen(id); setDirtyOpen(false);
    say(`Editing ${PANEL_NAME[id]}.${field ? ` ${field}.` : ''}`);
    requestAnimationFrame(() => document.getElementById(`panel-${id}`)?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
  }
  function closePanel() { setOpen(null); setDirtyOpen(false); setFocusField(null); setState(IDLE); }
  function cancel(dirty: boolean) { if (dirty) setDiscard(() => closePanel); else closePanel(); }

  async function save(patch: DoctorPatch, panel: PanelId): Promise<boolean> {
    if (!d) return false;
    if (Object.keys(patch).length === 0) { closePanel(); return true; }
    if (patch.reportTypes && d.reportTypes.some((t) => !patch.reportTypes!.includes(t))) {
      const dropped = d.reportTypes.filter((t) => !patch.reportTypes!.includes(t));
      const consequential = dropped.filter((t) => hasOverlay(t) || (matters.data ?? []).some((m) => m.doctor.slug === slug && m.reportType === t));
      if (consequential.length > 0 && !dropType) { setDropType({ types: consequential, patch }); return false; }
    }
    setState({ ...IDLE, saving: true });
    try {
      const result = await update.mutateAsync({ patch, version: d.version });
      const at = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false });
      setState({ ...IDLE, savedAt: at });
      closePanel();
      toast.success(`Saved: ${PANEL_NAME[panel]} for ${d.name}`);
      say(`Saved. ${shortSentence(result.readiness[type])}`);
      return true;
    } catch (err) {
      const r = refusalOf(err);
      if (r.kind === 'conflict') setState({ ...IDLE, conflict: true });
      else if (r.kind === 'invalid' && r.reason) setState({ ...IDLE, error: `${r.reason.charAt(0).toUpperCase()}${r.reason.slice(1)}.` });
      else setState({ ...IDLE, error: 'The change could not be saved. Nothing changed. Try again.' });
      return false;
    }
  }

  async function setActive(active: boolean) {
    if (!d) return;
    setSwitching(true);
    try {
      await update.mutateAsync({ patch: { active }, version: d.version });
      toast.success(active ? `${d.name} is active.` : `${d.name} is inactive. Blocked for every report type.`);
    } catch (err) {
      const r = refusalOf(err);
      if (r.kind === 'conflict') { await reload(); toast.error(`${d.name} was changed by someone else. Reloaded; try again.`); } else toast.error(`Could not change ${d.name}'s active state. Nothing changed.`, { action: { label: 'Retry', onClick: () => void setActive(active) } });
    } finally {
      setSwitching(false);
    }
  }

  function onFix(check: ReadinessCheck) {
    // A not-decided check has no action here: its detail says what is pending (OPEN 76).
    if (check.state === 'not_decided') return;
    openPanel(PANEL_FOR[check.fix], FIELD_FOR[check.fix]);
  }

  const landed = (v: DoctorView) => { settle(v); };

  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return (
      <>
        <PageHeader eyebrow={<Eyebrow type={type} />} title="Doctor not found" documentTitle="Doctor not found" />
        <EmptyState className="mt-6" title={`No doctor '${slug}' is in the registry.`} action={<Link to={`/registry?type=${type}`} className={buttonVariants({ variant: 'outline' })}>Doctors</Link>} />
      </>
    );
  }
  if (query.isError && !d) {
    return (
      <>
        <PageHeader eyebrow={<Eyebrow type={type} />} title={slug} documentTitle={slug} />
        <ErrorPanel className="mt-6" message={`${slug} could not be loaded.`} onRetry={() => void query.refetch()} />
      </>
    );
  }
  if (!view || !d) {
    return (
      <div className="flex flex-col gap-6" role="status" aria-label={`Loading ${slug}`}>
        <PageHeader eyebrow={<Eyebrow type={type} />} title={slug} documentTitle={slug} meta={<Skeleton className="h-5 w-72" />} />
        <div className="rounded-xl border border-beam-line bg-beam-paper p-4"><Skeleton className="h-10 w-full rounded-full" /><Skeleton className="mt-3 h-7 w-64" />{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="mt-2 h-[46px] w-full" />)}</div>
        <Skeleton className="h-40 rounded-xl" /><Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  const readiness = view.readiness;
  const panelProps = (id: PanelId) => ({
    doctor: d, editing: open === id, canEdit: open === null, editable: !phone, offline, state: open === id ? state : IDLE,
    onEdit: () => openPanel(id), onCancel: cancel, onReload: () => { void reload(); setState({ ...IDLE }); },
    onSave: (patch: DoctorPatch) => save(patch, id), onLanded: landed, focusField: open === id ? focusField : null, phone, onDirty: (v: boolean) => { if (open === id) setDirtyOpen(v); },
  });

  const header = (
    <PageHeader
      eyebrow={<Eyebrow type={type} />}
      title={d.name}
      documentTitle={`${d.name} . Registry`}
      meta={metaLine(d).map((m, i) => <span key={i} data-slot={i === 1 ? 'active-meta' : undefined}>{m}</span>)}
      actions={(
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="outline" size="lg" className="h-11 md:h-9" aria-label={`More actions for ${d.name}`}>More</Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild><Link to={`/pipeline?doctor=${d.slug}`}>Open in pipeline</Link></DropdownMenuItem>
            <DropdownMenuSeparator />
            {d.active ? <DropdownMenuItem disabled={offline || phone} onSelect={() => setDeactivate(true)}>Deactivate</DropdownMenuItem> : <DropdownMenuItem disabled={offline || phone} onSelect={() => void setActive(true)}>Reactivate</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    />
  );

  if (phone) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <ReadinessCard readiness={readiness} type={type} onType={setType} />
        <DoctorPanel {...panelProps('doctor')} clinics={clinics.data ?? []} />
        <OnTheReportPanel {...panelProps('report')} />
        <TemplatesPanel {...panelProps('templates')} />
        <ProfilePanel {...panelProps('profile')} />
        <PhoneWorkspace doctor={d} open={sheet} onOpenChange={setSheet} offline={offline} onSave={(patch) => save(patch, 'workspace')} state={state} />
        <ChangeHistory audit={d.audit} phone />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      <ReadinessPanel doctor={d} readiness={readiness} type={type} onType={setType} onFix={onFix} editing={open !== null} offline={offline} />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <DoctorPanel {...panelProps('doctor')} clinics={clinics.data ?? []} />
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-beam-line bg-beam-paper px-4 py-3">
            <Switch id="active-switch" checked={d.active} disabled={offline} aria-busy={switching || undefined} aria-describedby="active-sentence" title={offline ? 'Paused while offline.' : undefined} onCheckedChange={(v) => { if (v) void setActive(true); else setDeactivate(true); }} />
            <label htmlFor="active-switch" className="text-sm font-medium">{d.active ? 'Active' : 'Inactive'}</label>
            <span id="active-sentence" className="text-xs text-beam-text-muted">Deactivating removes readiness for every case assigned to this doctor.</span>
          </div>
        </div>
        <OnTheReportPanel {...panelProps('report')} />
        <TemplatesPanel {...panelProps('templates')} />
        <ProfilePanel {...panelProps('profile')} />
        <WorkspacePanel {...panelProps('workspace')} />
        <ChangeHistory audit={d.audit} phone={false} />
      </div>

      <AlertDialog open={discard !== null} onOpenChange={(o) => { if (!o) { setDiscard(null); if (blocker.state === 'blocked') blocker.reset(); } }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Discard the changes to {open ? PANEL_NAME[open] : 'this panel'}?</AlertDialogTitle><AlertDialogDescription>The typed values are dropped and the doctor stays as saved.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const run = discard; setDiscard(null); closePanel(); run?.(); }}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deactivate} onOpenChange={setDeactivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {d.name}?</AlertDialogTitle>
            <AlertDialogDescription data-slot="dependants">
              {matters.isError ? 'The assigned cases could not be counted. ' : dependants.length === 0 ? `No case is assigned to ${d.name}. ` : `${pluralise(dependants.length, 'case is', 'cases are')} assigned to ${d.name}: ${byStatus(dependants.map((m) => m.status))}. `}
              While inactive, {d.name} is blocked for every report type and none of these cases can generate a blank. The configuration is kept; reactivating restores readiness.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} aria-disabled={switching ? 'true' : undefined} onClick={(e) => { e.preventDefault(); void setActive(false).then(() => setDeactivate(false)); }}>{switching ? 'Deactivating' : `Deactivate ${d.name}`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dropType !== null} onOpenChange={(o) => { if (!o) setDropType(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop offering {dropType ? dropType.types.map(longType).join(' and ') : ''} for {d.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {dropType ? dropType.types.map((t) => { const assigned = (matters.data ?? []).filter((m) => m.doctor.slug === slug && m.reportType === t); return `${assigned.length > 0 ? `${pluralise(assigned.length, 'case')} of type ${shortType(t)} ${assigned.length === 1 ? 'is' : 'are'} assigned: ${assigned.map((m) => `${m.ref} at ${m.status}`).join(', ')}. ` : ''}${hasOverlay(t) ? `The ${shortType(t)} overlay is kept and returns if the type is offered again.` : ''}`; }).join(' ') : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={(e) => { e.preventDefault(); const p = dropType!.patch; setDropType(null); void (async () => { setState({ ...IDLE, saving: true }); try { await update.mutateAsync({ patch: p, version: d.version }); closePanel(); toast.success(`Saved: ${PANEL_NAME.templates} for ${d.name}`); } catch { setState({ ...IDLE, error: 'The change could not be saved. Nothing changed. Try again.' }); } })(); }}>Stop offering {dropType ? dropType.types.map(shortType).join(', ') : ''}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Eyebrow({ type }: { type: ReportTypeKind }) {
  return <nav aria-label="Registry" className="flex items-center gap-1">Registry · <Link to={`/registry?type=${type}`} className="text-beam-signature underline-offset-4 hover:underline">Doctors</Link></nav>;
}

function byStatus(statuses: Status[]): string {
  const counts = new Map<Status, number>();
  for (const s of statuses) counts.set(s, (counts.get(s) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([s, n]) => `${n} at ${s} ${statusLabel(s)}`).join(', ');
}

/** The phone's Workspace: the read card with Edit opening a bottom sheet; the same patch as the desktop panel. */
function PhoneWorkspace({ doctor: d, open, onOpenChange, offline, onSave, state }: { doctor: DoctorView['doctor']; open: boolean; onOpenChange: (o: boolean) => void; offline: boolean; onSave: (patch: DoctorPatch) => Promise<boolean>; state: PanelSaveState }) {
  const [mode, setMode] = useState(d.workspace.dictationMode);
  const [terms, setTerms] = useState(d.workspace.keyterms.join('\n'));
  const readProof = d.workspace.proofreading !== false;
  const [proof, setProof] = useState(readProof);
  useEffect(() => { if (open) { setMode(d.workspace.dictationMode); setTerms(d.workspace.keyterms.join('\n')); setProof(d.workspace.proofreading !== false); } }, [open, d.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = mode !== d.workspace.dictationMode || terms !== d.workspace.keyterms.join('\n') || proof !== readProof;
  return (
    <>
      {/* The phone's one edit lives in the sheet; the panel's own Edit opens it. */}
      <WorkspacePanel doctor={d} editing={false} canEdit={!offline} editable offline={offline} state={state} onEdit={() => onOpenChange(true)} onCancel={() => {}} onReload={() => {}} onSave={onSave} onLanded={() => {}} focusField={null} phone onDirty={() => {}} />
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" showCloseButton={false} className="max-h-[85svh] rounded-t-[20px] shadow-beam-3">
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
          <SheetHeader className="pb-0"><SheetTitle className="text-base">Workspace</SheetTitle><SheetDescription className="sr-only">Dictation mode, key terms and proofreading</SheetDescription></SheetHeader>
          <form className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4" onSubmit={(e) => { e.preventDefault(); void onSave({ ...(mode !== d.workspace.dictationMode ? { dictationMode: mode } : {}), ...(terms !== d.workspace.keyterms.join('\n') ? { keyterms: [...new Set(terms.split('\n').map((s) => s.trim()).filter(Boolean))] } : {}), ...(proof !== readProof ? { proofreading: proof } : {}) }).then((ok) => { if (ok) onOpenChange(false); }); }}>
            <WorkspaceFields id="phone-workspace" mode={mode} onMode={setMode} terms={terms} onTerms={setTerms} proofreading={proof} onProofreading={setProof} phone />
            {state.error ? <p role="alert" className="text-[13px] text-beam-error">{state.error}</p> : null}
            <SheetFooter className="flex-row gap-2 px-0">
              <Button type="button" variant="outline" className="h-11 flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="h-11 flex-1" disabled={offline || !dirty || state.saving}>{state.saving ? 'Saving' : 'Save'}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
